import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dumbbell, ArrowLeft, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Velkommen tilbage!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Forkert email eller adgangskode");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NjA3ODB8MHwxfHNlYXJjaHwyfHxneW0lMjB3b3Jrb3V0fGVufDB8fHx8MTcwOTMwMDAwMHww&ixlib=rb-4.0.3&q=85"
            alt="Workout"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/90" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-lg bg-[#DFFF00] flex items-center justify-center">
              <Dumbbell className="h-6 w-6 text-black" />
            </div>
            <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl uppercase tracking-wider text-white">
              Fitrack<span className="text-[#DFFF00]">R</span>
            </span>
          </div>

          <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-4xl xl:text-5xl font-bold uppercase text-white leading-tight mb-6">
            Velkommen<br />
            <span className="text-[#DFFF00]">tilbage</span>
          </h2>

          <p className="text-zinc-400 text-lg max-w-md">
            Log ind og fortsæt din fitnessrejse. Dine fremskridt venter på dig.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-8 text-zinc-500 hover:text-white transition-colors text-sm" data-testid="login-back">
            <ArrowLeft className="h-4 w-4" /> Tilbage til forsiden
          </Link>

          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Dumbbell className="h-6 w-6 text-[#DFFF00]" />
            <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wider text-white">
              Fitrack<span className="text-[#DFFF00]">R</span>
            </span>
          </div>

          <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl uppercase tracking-tight text-white mb-2" data-testid="login-title">
            Log ind
          </h1>
          <p className="text-zinc-400 mb-8">
            Indtast dine oplysninger for at fortsætte.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Email</Label>
              <Input 
                id="email" 
                type="email" 
                value={email} 
                onChange={e => setEmail(e.target.value)} 
                required
                className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] focus:ring-[#DFFF00]/20 rounded-lg h-12 text-white placeholder:text-zinc-600" 
                data-testid="login-email" 
                placeholder="din@email.dk" 
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500">Adgangskode</Label>
                <Link to="/forgot-password" className="text-xs text-[#DFFF00] hover:underline" data-testid="forgot-password-link">
                  Glemt adgangskode?
                </Link>
              </div>
              <Input 
                id="password" 
                type="password" 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                required
                className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] focus:ring-[#DFFF00]/20 rounded-lg h-12 text-white placeholder:text-zinc-600" 
                data-testid="login-password" 
                placeholder="Din adgangskode" 
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading} 
              data-testid="login-submit"
              className="w-full bg-[#DFFF00] text-black hover:bg-[#C8E600] hover:shadow-[0_0_30px_rgba(223,255,0,0.3)] rounded-lg font-bold uppercase tracking-wider h-14 text-base transition-all duration-300"
            >
              {loading ? (
                "Logger ind..."
              ) : (
                <>
                  <LogIn className="mr-2 h-5 w-5" />
                  Log ind
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-zinc-800">
            <p className="text-center text-sm text-zinc-500">
              Har du ikke en konto?{" "}
              <Link to="/register" className="text-[#DFFF00] hover:underline font-medium" data-testid="login-register-link">
                Opret gratis konto
              </Link>
            </p>
            <p className="text-center text-xs text-zinc-600 mt-2">
              3 dages gratis prøveperiode inkluderet
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
